import { RelativeDateOptions } from 'common/util/dateRanges';
import { dayjs } from 'common/util/dayjsUtils';
import decodeQueryTextParam from 'common/util/decodeQueryTextParam';
import mapify from 'common/util/mapify';
import queryString from 'common/util/queryString';
import validateInput from 'common/validateInput';

const SortMap = {
  mrr: 'mrr',
  new: 'newest',
  old: 'oldest',
  opportunity: 'opportunity',
  statusChanged: 'statusChanged',
  top: 'score',
  trending: 'trendingScore',
};

// Helper Functions

/**
 * Sanitizes a filter query string by removing empty values and cleaning up
 * parameters that get split by underscores (e.g., "categories=" or "tags=_")
 */
export function sanitizeFilterQuery(filterQuery) {
  if (!filterQuery || filterQuery === '?') {
    return '';
  }

  const parsed = queryString.parse(filterQuery);
  const sanitized = { ...parsed };

  // Remove all empty string values
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === '') {
      delete sanitized[key];
    }
  });

  // Clean up parameters that get split by underscores
  const underscoreSplitParams = ['categories', 'tags', 'boards', 'companies', 'status', 'owner'];
  underscoreSplitParams.forEach((param) => {
    if (sanitized[param]) {
      const array = sanitized[param].split('_').filter(Boolean);
      if (array.length === 0) {
        delete sanitized[param];
      } else {
        sanitized[param] = array.join('_');
      }
    }
  });

  return queryString.stringify(sanitized);
}

function formatDateRangeString(dateRangeString) {
  let dateRange = dateRangeString.split('_');
  if (dateRange.length === 1 && RelativeDateOptions[dateRange[0]]) {
    // It's a relative date, so let's get the date range based on today.
    dateRange = RelativeDateOptions[dateRange[0]].toRange();
  }
  if (dateRange.length === 2) {
    // Validate dates before formatting
    const fromDate = dayjs(dateRange[0]);
    const toDate = dayjs(dateRange[1]);

    // Check if dates are valid and in correct order
    if (
      fromDate.isValid() &&
      toDate.isValid() &&
      validateInput.primitives.isoString(fromDate.format()) &&
      validateInput.primitives.isoString(toDate.format()) &&
      (fromDate.isBefore(toDate) || fromDate.isSame(toDate))
    ) {
      return [
        fromDate.utcOffset(0).startOf('day').utc().format(),
        toDate.utcOffset(0).endOf('day').utc().format(),
      ];
    }
    // Invalid dates - return undefined to skip the filter
    return undefined;
  }
  // Invalid format - return undefined
  return undefined;
}

export function formatCustomQueries(queries) {
  try {
    const decodedQueries = JSON.parse(decodeURIComponent(queries));
    return Object.keys(decodedQueries).map((queryKey) => ({
      fieldID: queryKey,
      operator: decodedQueries[queryKey].operator,
      value: decodedQueries[queryKey].value,
    }));
  } catch {
    return [];
  }
}

export function getQueryFilterParams(board, company, location, params, roadmap) {
  const { segments } = company;
  const { query, state } = location;
  const similarPostSuggestions =
    state && state.createForm && (state.createForm.title || state.createForm.details);

  var textSearch = null;
  if (similarPostSuggestions) {
    textSearch = ((state.createForm.title || '') + ' ' + (state.createForm.details || '')).trim();
  } else if (typeof query.search === 'string') {
    textSearch = decodeQueryTextParam(query.search)?.trim();
  }

  const segment = segments && segments.find(({ urlName }) => urlName === query.segment);
  const isAdminFeedbackView = location.pathname.startsWith('/admin/feedback');

  const searchAllBoards = !params.isWidget && textSearch && company.enableGlobalPostSearch;
  const pages = params.pages;

  // example param description : { 601c400ebbb357463aad3f70: { operator: 'contains', value: 'word' }}
  const descriptionColumnFilters = query.description ? formatCustomQueries(query.description) : [];
  const factorFilters = query.factor ? formatCustomQueries(query.factor) : [];

  // The difference between `currentBoard` and `board` is that `board` is used to indicate a specific board should be queried.
  // `currentBoard` indicates to the API that the user is viewing a board, but not that the board should be necessarily queried.
  // This is used in the case of searching all boards, indicating that the current board, even if it's unlisted, can be searched.
  return {
    ...(!isAdminFeedbackView && board && { currentBoard: board }),
    ...(!isAdminFeedbackView && !searchAllBoards && board && { board }),
    ...(textSearch && { textSearch }),
    ...(pages && { pages }),
    ...(!similarPostSuggestions && {
      ...(!textSearch && typeof query.category === 'string' && { category: query.category }),
      ...(typeof query.boards === 'string' && { boards: query.boards }),
      ...(typeof query.categories === 'string' && { categories: query.categories }),
      ...(typeof query['post-created'] === 'string' && { ['post-created']: query['post-created'] }),
      ...(typeof query['vote-created'] === 'string' && { ['vote-created']: query['vote-created'] }),
      ...(typeof query['eta'] === 'string' && { ['eta']: query['eta'] }),
      ...(query.filter && { filter: query.filter }),
      ...(query.owner && { owner: query.owner }),
      ...(segment && { segment: segment.urlName }),
      ...(query.sort && { sort: query.sort }),
      ...(typeof query.status === 'string' && { status: query.status }),
      ...(typeof query.tags === 'string' && { tags: query.tags }),
      ...(typeof query.companies === 'string' && { companies: query.companies }),
      ...(typeof query.accountOwner === 'string' && { accountOwner: query.accountOwner }),
      ...(query.unassigned && { unassigned: true }),
      ...(query.uncategorized && { uncategorized: true }),
      ...(query.untagged && { untagged: true }),
      ...(descriptionColumnFilters.length && { descriptionColumnFilters }),
      ...(factorFilters.length && { factorFilters }),
      ...(roadmap && { roadmap }),
    }),
  };
}

export function mapQueryFilterParamsToRequestData(
  queryParams,
  company,
  { supplementalOwners, boards } = {},
) {
  const requestData = {
    pages: queryParams.pages ? queryParams.pages : 1,
    sort: SortMap[queryParams.sort] || (queryParams.textSearch ? undefined : 'trendingScore'),
  };
  if (queryParams.currentBoard) {
    requestData.currentBoard = queryParams.currentBoard.urlName;
  }
  if (typeof queryParams.boards !== 'undefined') {
    if (queryParams.boards === '') {
      // Don't set boardURLNames - will default to all boards
    } else {
      const boardURLNames = queryParams.boards.split('_').filter((boardURLName) => {
        return !!boardURLName;
      });

      // If boards are provided, validate against them
      if (boards && boards.length > 0) {
        const boardsMap = mapify(boards, 'urlName');
        const validBoardURLNames = boardURLNames.filter((boardURLName) => {
          return !!boardsMap[boardURLName];
        });
        // Only set boardURLNames if we have valid boards
        // If all boards are invalid, don't set it (will default to all boards)
        if (validBoardURLNames.length > 0) {
          requestData.boardURLNames = validBoardURLNames;
        }
      } else if (boardURLNames.length > 0) {
        // If boards aren't available for validation, use the filtered list
        requestData.boardURLNames = boardURLNames;
      }
    }
  } else if (queryParams.board) {
    requestData.boardURLNames = [queryParams.board.urlName];
  }
  if (typeof queryParams.category !== 'undefined') {
    requestData.categoryURLNames = [queryParams.category];
  }
  if (queryParams.postID) {
    requestData.postID = queryParams.postID;
  }
  if (typeof queryParams.categories !== 'undefined') {
    // Filter out empty strings (e.g., categories=_)
    const categoryURLNames = queryParams.categories.split('_').filter(Boolean);
    if (categoryURLNames.length > 0) {
      requestData.categoryURLNames = categoryURLNames;
    }
  }
  if (typeof queryParams.companies !== 'undefined') {
    // Filter out empty strings (e.g., companies=_)
    const companyURLNames = queryParams.companies.split('_').filter(Boolean);
    if (companyURLNames.length > 0) {
      requestData.companyURLNames = companyURLNames;
    }
  }
  if (typeof queryParams.accountOwner !== 'undefined') {
    requestData.accountOwnerName = queryParams.accountOwner;
  }
  if (queryParams['post-created']) {
    const dateRangeString = queryParams['post-created'];
    const dateRange = formatDateRangeString(dateRangeString);
    // Only set date range if valid (similar to boards/statuses handling)
    if (dateRange) {
      requestData.postCreatedDateRange = dateRange;
    }
  }
  if (queryParams['vote-created']) {
    const dateRangeString = queryParams['vote-created'];
    const dateRange = formatDateRangeString(dateRangeString);
    // Only set date range if valid (similar to boards/statuses handling)
    if (dateRange) {
      requestData.voteCreatedDateRange = dateRange;
    }
  }
  if (queryParams['eta']) {
    const dateRangeString = queryParams['eta'];
    const dateRange = formatDateRangeString(dateRangeString);
    // Only set date range if valid (similar to boards/statuses handling)
    if (dateRange) {
      requestData.etaDateRange = dateRange;
    }
  }
  if (queryParams.filter === 'my') {
    requestData.my = true;
  }
  if (queryParams.filter === 'myCompanies') {
    requestData.myCompanies = true;
  }
  if (queryParams.roadmap) {
    requestData.roadmapID = queryParams.roadmap._id;
  }
  if (queryParams.owner) {
    // Filter out empty strings (e.g., owner=_)
    const owners = queryParams.owner.split('_').filter(Boolean);
    if (owners.length > 0) {
      const membersMap = mapify(company.members, 'urlName');
      const supplementalOwnersMap =
        supplementalOwners?.length > 0 ? mapify(supplementalOwners, 'urlName') : null;

      const ownerIDs = [];
      owners.forEach((owner) => {
        const member = membersMap[owner];
        if (member) {
          ownerIDs.push(member._id);
        } else if (supplementalOwnersMap?.[owner]) {
          ownerIDs.push(supplementalOwnersMap[owner]._id);
        }
      });

      // Only set ownerIDs if we have valid owners
      // If all owners are invalid, don't set it (backend will handle gracefully)
      if (ownerIDs.length > 0) {
        requestData.ownerIDs = ownerIDs;
      }
    }
  }
  if (queryParams.scoreFactor) {
    requestData.scoreFactor = queryParams.scoreFactor;
  }
  if (queryParams.segment) {
    requestData.segmentURLName = queryParams.segment;
  }
  if (typeof queryParams.status !== 'undefined') {
    const statusMap = mapify(company.statuses, 'urlName');
    const statusNames = queryParams.status
      .split('_')
      .map((statusURLName) => {
        return statusMap[statusURLName]?.name;
      })
      .filter(Boolean);
    // Only set status filter if we have valid status names
    // Empty array would cause server to return no posts
    if (statusNames.length > 0) {
      requestData.status = {
        $in: statusNames,
      };
    }
  }
  if (typeof queryParams.tags !== 'undefined') {
    requestData.tagURLNames = queryParams.tags.split('_').filter(Boolean);
  }
  if (queryParams.textSearch) {
    requestData.textSearch = queryParams.textSearch;
  }
  if (queryParams.unassigned) {
    requestData.unassigned = true;
  }
  if (queryParams.uncategorized) {
    requestData.uncategorized = true;
  }
  if (queryParams.untagged) {
    requestData.untagged = true;
  }
  if (queryParams.descriptionColumnFilters) {
    requestData.descriptionColumnFilters = queryParams.descriptionColumnFilters;
  }
  if (queryParams.factorFilters) {
    requestData.factorFilters = queryParams.factorFilters;
  }
  return requestData;
}

export function getPostQueryKey(queryParams) {
  const queryObject = {};
  if (queryParams.currentBoard) {
    queryObject.currentBoard = queryParams.currentBoard.urlName;
  }
  if (typeof queryParams.boards !== 'undefined') {
    queryObject.boardURLNames = queryParams.boards;
  } else if (queryParams.board) {
    queryObject.boardURLNames = queryParams.board.urlName;
  }
  if (typeof queryParams.category !== 'undefined') {
    queryObject.categories = queryParams.category;
  }
  if (typeof queryParams.companies !== 'undefined') {
    queryObject.companyURLNames = queryParams.companies;
  }
  if (typeof queryParams.accountOwner !== 'undefined') {
    queryObject.accountOwnerName = queryParams.accountOwner;
  }
  if (queryParams.postID) {
    queryObject.postID = queryParams.postID;
  }
  if (typeof queryParams.categories !== 'undefined') {
    queryObject.categories = queryParams.categories;
  }
  if (typeof queryParams['post-created'] !== 'undefined') {
    queryObject['post-created'] = queryParams['post-created'];
  }
  if (typeof queryParams['vote-created'] !== 'undefined') {
    queryObject['vote-created'] = queryParams['vote-created'];
  }
  if (typeof queryParams['eta'] !== 'undefined') {
    queryObject['eta'] = queryParams['eta'];
  }
  if (typeof queryParams.filter !== 'undefined') {
    queryObject.filter = queryParams.filter;
  }
  if (typeof queryParams.owner !== 'undefined') {
    queryObject.owner = queryParams.owner;
  }
  if (typeof queryParams.query !== 'undefined') {
    queryObject.query = queryParams.query;
  }
  if (queryParams.scoreFactor) {
    queryObject.scoreFactor = queryParams.scoreFactor;
  }
  if (queryParams.segment) {
    queryObject.segment = queryParams.segment;
  }

  if (typeof queryParams.status !== 'undefined') {
    queryObject.status = queryParams.status;
  }
  if (typeof queryParams.tags !== 'undefined') {
    queryObject.tags = queryParams.tags;
  }
  if (queryParams.textSearch) {
    queryObject.textSearch = queryParams.textSearch;
  }
  if (queryParams.unassigned) {
    queryObject.unassigned = queryParams.unassigned;
  }
  if (queryParams.uncategorized) {
    queryObject.uncategorized = queryParams.uncategorized;
  }
  if (queryParams.untagged) {
    queryObject.untagged = queryParams.untagged;
  }

  if (queryParams.roadmap) {
    queryObject.roadmap = queryParams.roadmap._id;
  }

  if (!queryParams.roadmap) {
    queryObject.sort = queryParams.sort || (queryParams.textSearch ? 'relevance' : 'trending');
  }

  if (queryParams.descriptionColumnFilters) {
    queryObject.descriptionColumnFilters = queryParams.descriptionColumnFilters;
  }

  if (queryParams.factorFilters) {
    queryObject.factorFilters = queryParams.factorFilters;
  }

  return JSON.stringify(queryObject);
}
